<template>
    <div>
        <div class="row">
            <div class="col-12">
                <div class="info">
                    <div class="label">Subtotal:</div>

                    <div class="content">
                        {{ formatValue(getSubtotal) }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="items.length && getServiceFeeTotal > 0" class="row">
            <div class="col-12">
                <div class="info">
                    <div class="label">Taxa de serviço:</div>

                    <div class="content">
                        {{ formatValue(getServiceFeeTotal) }}
                    </div>
                </div>
            </div>
        </div>

        <div v-if="coupon" class="row">
            <div class="col-12">
                <div class="info">
                    <div class="label">Cupom:</div>

                    <div class="content">
                        (<a href="#" v-on:click="onRemoveCoupon()">remover</a>) {{ getCouponValue(coupon, true) }}
                    </div>
                </div>
                
                <div class="coupon-product-info" v-if="itensWithCoupon.length !== items.length">
                    <div class="message">
                        <strong>Cupom aplicado aos itens:</strong> 
                        <div class="product" v-for="(item, index) in itensWithCoupon" :key="index">
                            {{item.product.name}} {{getItemDateTimeLabel(item)}}<span v-if="index+1 < itensWithCoupon.length">, </span>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="col-12">
                <div class="info total">
                    <div class="label">Total:</div>

                    <div class="content">
                        {{ formatValue(getTotal) }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import Select from '../../../Select';
import SellerApi from '../../../../services/SellerApi';
import { couponIsAppliedToItem } from '../../../../base/vue';

export default {
    components: { Select },
    props: {
        items: {
            type: Array,
            required: true,
        },
        coupon: {
            type: Object,
            required: false,
            default: null,
        },
        onRemoveCoupon: {
            type: Function,
            required: false,
            default: ()=>{},
        },
        appId: {
            type: String,
            required: false,
            default: null,
        },
        appCode: {
            type: String,
            required: true,
        },
    },
    data(){
        return {
            arrangedProductsLoading: false,
            arrangedProducts: [],
        };
    },
    computed: {
        getSubtotal() {
            return this.items.reduce((total, item) => {
                return total + this.cartItemValue(item);
            }, 0);
        },
        getTotal() {
            const subtotal = this.getSubtotal;
            const serviceFee = this.getServiceFeeTotal;

            let couponDiscount = 0;
            let couponDiscounts = null;

            if (this.coupon) {
                couponDiscounts = this.getCouponProductDiscount(this.coupon);
                couponDiscount = couponDiscounts.reduce((carry, discount) => carry + discount, 0) ;
            }

            const total = (subtotal - couponDiscount) + serviceFee;

            if (total < 0) {
                return 0;
            }

            return total
        },
        itensWithCoupon() {
            return this.items.filter(item => this.coupon ? couponIsAppliedToItem(this.coupon, item, this.appId) : false)
        },
        getServiceFeeTotal() {
            let total = 0;
            const amountCentsChargedGroupIds = new Set();

            for (const item of this.items) {
                if (this.isThirdPartyItem(item) && !this.shouldChargeServiceFeeForItem(item)) {
                    continue;
                }

                const selections = item.tariffs || [];
                for (const selection of selections) {
                    const tariff = selection.tariff;
                    if (!tariff?.id) continue;

                    const group = this.findTariffGroupForTariff(item.product, tariff.id);
                    const fee = group?.service_fee;
                    if (!fee) continue;

                    total += this.getServiceFeeContributionForSelection(
                        fee,
                        tariff,
                        selection,
                        group,
                        amountCentsChargedGroupIds
                    );
                }
            }

            return total;
        },
    },
    methods: {
        getServiceFeeContributionForSelection(fee, tariff, selection, group, amountCentsChargedGroupIds) {
            const priceCents = tariff.price_cents ?? tariff.priceCents ?? 0;
            const qty = parseInt(selection.amount, 10) || 0;
            const lineValueCents = priceCents * qty;

            if (fee.fee_type === 'percentage_fee') {
                const percentage = Number(fee.fee_percentage) || 0;
                return Math.floor(lineValueCents * percentage);
            }
            if (fee.fee_type === 'amount_cents') {
                const groupId = String(group.id);
                if (!amountCentsChargedGroupIds.has(groupId)) {
                    amountCentsChargedGroupIds.add(groupId);
                    return Number(fee.fee_amount_cents) || 0;
                }
            }
            return 0;
        },
        isThirdPartyItem(item) {
            return String(item.product?.id_app) !== String(this.appId);
        },
        shouldChargeServiceFeeForItem(item) {
            const productId = item.product?.id != null ? String(item.product.id) : null;    
            if (!productId) {
                return false;
            }

            const arrangedProduct = (this.arrangedProducts || []).find(
                (arrangedProduct) => String(arrangedProduct.product_id) === productId
            );

            const partnershipItem = arrangedProduct?.partnership_item;
            if (!partnershipItem) {
                return false;
            }

            return !!partnershipItem.chargeServiceFee;
        },
        findTariffGroupForTariff(product, tariffId) {
            const id = String(tariffId);
            const groups = product?.tariff_groups || [];
            for (const g of groups) {
                const tariffs = g.tariffs || [];
                if (tariffs.some((t) => String(t.id) === id)) {
                    return g;
                }
            }
            return null;
        },
        getItemDateTimeLabel(item) {
            if (item.product.selling_mode === "with_date_and_time") {
                return `(${item.date.date} ${item.time.label})`;
            }

            if (item.product.selling_mode === "with_date_only") {
                return `(${item.date.date})`;
            }

            return '';
        },
        formatValue(value) {
            if (value) {
                return (value / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
            }
            const zero = 0

            return zero.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        },
        cartItemValue(item) {
            return item.totalSummaryValue;
        },
        spreadDiscountIntoCartItems(cartItems) {
            // Calculate the total value of all cart items
            const totalValue = cartItems.reduce((carry, cartItem) => carry + cartItem.totalSummaryValue, 0);
            const couponValue = this.coupon.discount_amount_cents;

            // If the total value is 0 or the coupon value is 0, return an empty object of discounts
            if (totalValue === 0 || couponValue === 0) {
                return {};
            }

            return cartItems.map((cartItem) => {
                // Calculate the discount ratio for the cart item
                const itemDiscountRatio = cartItem.totalSummaryValue / totalValue;

                // Calculate the maximum discount allowed for the cart item
                const maxCartItemDiscount = cartItem.totalSummaryValue;

                // Calculate the discount for the cart item based on its ratio
                let cartItemDiscount = couponValue * itemDiscountRatio;

                // Ensure that the discount does not exceed the cart item's value
                cartItemDiscount = Math.min(cartItemDiscount, maxCartItemDiscount);

                // Store the cart item's id and calculated discount
                return cartItemDiscount;
            });
        },
        getCouponGeneralDiscount(coupon, subtotal){
            if (coupon.type === 'percentage') {
                return subtotal * coupon.discount_percentage / 100;
            }

            return coupon.discount_amount_cents;
        },
        getCouponProductDiscount(coupon){
            if (coupon.type === 'percentage') {
                return this.itensWithCoupon.map((cartItem) => cartItem.totalSummaryValue * coupon.discount_percentage / 100);
            }
            
            return this.spreadDiscountIntoCartItems(this.itensWithCoupon);
        },
        getCouponValue(coupon, formatted=false) {
            let value = coupon.discount_amount_cents / 100

            if (coupon.type === 'percentage') {
                value = coupon.discount_percentage

                if (formatted) {
                    return `${value}%`
                }

                return value
            }

            if (formatted) {
                return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            }
            
            return value
        },
        getArrangedProducts() {
            this.arrangedProductsLoading = true;

            SellerApi.Products.arrangedProducts(this.appCode, {
                include: ['partnershipItem'],
                associationModels: ['resale'],
            })
                .then((data) => {
                    this.arrangedProductsLoading = false;
                    this.arrangedProducts = data
                })
                .catch(err => {
                    this.arrangedProductsLoading = false;
                    console.error(err)
                });
        },
    },
    mounted() {
        this.getArrangedProducts();
    },
}
</script>

<style scoped lang="scss">
.info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 5px;
    font-size: 16px;

    &.total {
        font-weight: bold;
    }

    &:last-child {
        margin-bottom: 0;
    }
}

.coupon-product-info {
    .message {
        font-size: 12px;
        color: #666;

        .product {
            display: inline-block;
        }
    }
}

</style>
